import React, { useState } from 'react';
import { lightTheme as T } from '../Constants/Colors';
import { fontFamily, fontSize, fontWeight } from '../Constants/Fonts';

// StatCard — the shared KPI / stat tile used across dashboards.
// Layout: uppercase label on top, a large (optionally coloured) value,
// and a small muted hint underneath. Fully theme-driven from the JS
// tokens in Constants/Colors + Constants/Fonts so it renders correctly
// regardless of which module stylesheet happens to be loaded.
//
// Props:
//   label      — small uppercase caption (top)
//   value      — the big number / stat
//   hint       — optional muted one-liner under the value
//   hintColor  — override the hint colour (defaults to muted grey) — e.g. a
//                red/green trend indicator in the hint text
//   valueColor — override the value colour (defaults to primary text)
//   valueSize  — override the value font size (px). Defaults to fontSize.xl.
//                Use a larger value (e.g. 36) for a headline "big number" tile.
//   labelBelow — render the value first and the label underneath it (the
//                big-number-on-top, caption-below layout). Default false keeps
//                the original label-on-top order, so existing usages are
//                unchanged.
//   accent     — optional left accent bar colour
//   icon       — optional node rendered right-aligned & vertically centred
//                (e.g. a <ModuleIcon />), with the text column on the left
//   iconLeft   — render the icon on the LEFT of the text column (vertically
//                centred), instead of the default right-aligned position. Use
//                for the tinted "icon + big number" KPI style. Default false.
//   onClick    — makes the card interactive (pointer + hover lift)
//   title      — native tooltip on the card
//   className  — extra class(es) on the root (e.g. `cmn_elevated_card` to give
//                the tile a stronger lifted shadow). When passed, the resting
//                shadow is left to the class so it can take effect; the hover
//                lift still applies inline.
//   style      — escape hatch for one-off overrides
function StatCard({ label, value, hint, hintColor, valueColor, valueSize, labelBelow, accent, icon, iconLeft, onClick, title, className, style }) {
    const [hover, setHover] = useState(false);
    const interactive = typeof onClick === 'function';

    const labelEl = (
        <div style={{
            fontFamily, fontSize: fontSize.xs, fontWeight: fontWeight.bold,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            color: T.textMuted,
            marginBottom: labelBelow ? 0 : 6,
            marginTop: labelBelow ? 8 : 0,
        }}>{label}</div>
    );
    const valueEl = (
        <div style={{
            fontFamily, fontSize: valueSize || fontSize['xl'], fontWeight: fontWeight.semibold,
            color: valueColor || T.textPrimary, lineHeight: 1.1, letterSpacing: '-0.01em',
        }}>{value}</div>
    );

    const textCol = (
        <div style={{ minWidth: 0, flex: 1 }}>
            {labelBelow ? <>{valueEl}{labelEl}</> : <>{labelEl}{valueEl}</>}
            {hint != null && hint !== '' && (
                <div style={{
                    fontFamily, fontSize: fontSize.xs, color: hintColor || T.textMuted, marginTop: 4,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }} title={typeof hint === 'string' ? hint : undefined}>{hint}</div>
            )}
        </div>
    );

    return (
        <div
            onClick={onClick}
            title={title}
            className={className}
            onMouseEnter={interactive ? () => setHover(true) : undefined}
            onMouseLeave={interactive ? () => setHover(false) : undefined}
            style={{
                background: T.surface,
                border: `1px solid ${hover ? T.borderStrong : T.border}`,
                borderLeft: accent ? `3px solid ${accent}` : `1px solid ${hover ? T.borderStrong : T.border}`,
                borderRadius: T.radiusLg,
                // With a className (e.g. cmn_elevated_card) the resting shadow is
                // left to the class; without one, keep the default subtle shadow.
                boxShadow: hover ? T.shadow : (className ? undefined : T.shadowSm),
                padding: '12px 14px',
                minWidth: 0,
                cursor: interactive ? 'pointer' : 'default',
                transition: 'box-shadow .15s ease, border-color .15s ease',
                ...style,
            }}
        >
            {icon ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {iconLeft ? (
                        <>
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</div>
                            {textCol}
                        </>
                    ) : (
                        <>
                            {textCol}
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</div>
                        </>
                    )}
                </div>
            ) : textCol}
        </div>
    );
}

export default StatCard;
